'use client'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { AddToCartCta } from '../_shared/AddToCartCta'
import type { CustomPageProps } from '../_shared/types'

type VolKey = 'v1' | 'v2' | 'v3'

export function WayneDobsonVolumes(
  props: Pick<
    CustomPageProps,
    | 'productId'
    | 'productName'
    | 'options'
    | 'hasDownloads'
    | 'isPreorder'
    | 'preorderReleaseDate'
  > & {
    /** Legacy PHOTO-7, PHOTO-6, PHOTO-8 for volumes 1–3 side thumbs */
    volumeThumbs: [string | undefined, string | undefined, string | undefined]
  }
) {
  const [open, setOpen] = useState<Record<VolKey, boolean>>({
    v1: false,
    v2: false,
    v3: false,
  })

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 992px)')
    const apply = () => {
      if (mq.matches) {
        setOpen({ v1: true, v2: true, v3: true })
      } else {
        setOpen({ v1: false, v2: false, v3: false })
      }
    }
    apply()
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [])

  const toggle = (key: VolKey) => {
    setOpen(o => ({ ...o, [key]: !o[key] }))
  }

  const renderCta = () => (
    <AddToCartCta
      productId={props.productId}
      productName={props.productName}
      options={props.options}
      hasDownloads={props.hasDownloads}
      isPreorder={props.isPreorder}
      preorderReleaseDate={props.preorderReleaseDate}
      label="Add to cart"
    />
  )

  const [t1, t2, t3] = props.volumeThumbs

  return (
    <>
      <div style={{ background: '#f2f2f2' }}>
        <div className="max-w-screen-xl mx-auto px-4 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="hidden lg:block lg:col-span-4">
              {t1 && (
                <div className="aspect-video w-full">
                  <img
                    src={t1}
                    alt="Wayne Dobson biography"
                    className="w-full h-full object-cover rounded border-4 border-[#351056] cursor-pointer hover:opacity-50 transition-opacity"
                  />
                </div>
              )}
            </div>
            <div className="lg:col-span-8">
              <h2 className="text-3xl md:text-4xl font-bold text-[#BB2926] mb-5">
                Volume 1: Memoirs
              </h2>
              <p className="text-xl font-medium leading-relaxed mb-4">
                Before you dive into the tricks in Volumes II & III, you&apos;ll be taken on
                a firsthand adventure through Wayne&apos;s remarkable lifelong journey. Step
                into his world and discover how a childhood fascination with magic in a drab
                suburban town blossomed into an obsession that drove his rise to superstardom.
                Tucked between the lines, you&apos;ll find invaluable pieces of wisdom that can
                help guide any aspiring performer to greatness.
              </p>
              <p className="mb-4">
                Told in his own voice, this uncensored view of Wayne&apos;s life is unlike
                the dry, data-dense biographies of magic&apos;s golden-age heroes. You&apos;ll
                be there step-by-step as he relives the highs of his career, while also not
                shying away from the challenges he&apos;s had to overcome and still faces to
                this day.
              </p>
              <p className="mb-4">
                During the darkest moments of his life, when his personal issues were thrust
                into the public eye and an MS diagnosis changed his life overnight, Wayne
                could have easily entered into a Hollywood-esque downward spiral. However, as
                you&apos;ll learn, he instead chose to face adversity with humor and magic.
              </p>
              <p className="mb-4">
                At his professional peak, Wayne was a role model and magic star. But
                throughout this inspirational story, you&apos;ll discover he&apos;s so much
                more.
              </p>
              <p className="mb-4">He is a survivor. He is an adaptor. He is a leader.</p>

              <button
                type="button"
                className="text-blue-600 underline lg:hidden mb-3"
                onClick={() => toggle('v1')}
              >
                View Volume 1 contents
              </button>
              <div className={`${open.v1 ? 'block' : 'hidden'} lg:block`}>
                <h4 className="font-bold text-lg mt-4 mb-2">Contents of Volume 1</h4>
                <ul className="columns-1 md:columns-2 gap-10 list-disc pl-5 space-y-1 text-base">
                  <li>Foreword (Andi Gladwin and Joshua Jay)</li>
                  <li>Introduction (Joe Pasquale)</li>
                  <li>My Story</li>
                  <li>In the Beginning</li>
                  <li>Early Days</li>
                  <li>Learning My Craft</li>
                  <li>Paying My Dues</li>
                  <li>On the Circuit</li>
                  <li>Rising Starr</li>
                  <li>Which Do You Want First?</li>
                  <li>The Royal Box</li>
                  <li>On the Box</li>
                  <li>Viva Las Vegas</li>
                  <li>A Kind of Magic</li>
                  <li>On the Road</li>
                  <li>The Show Must Go On</li>
                  <li>In Sickness &amp; in Health</li>
                  <li>Coming Out</li>
                  <li>Squeaky Clean</li>
                  <li>Out of the Frying Pan</li>
                  <li>Wheeler Dealer</li>
                  <li>I Wish I Could Fly</li>
                  <li>Shit Happens</li>
                  <li>Closure</li>
                </ul>
              </div>

              <p className="mt-6 flex flex-wrap gap-3">
                {renderCta()}
                <Link
                  href="/magic-books/wayne-dobsons-legacy-volume-1/"
                  className="inline-flex items-center justify-center rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg font-medium"
                >
                  Or buy Volume 1 separately
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="max-w-screen-xl mx-auto px-4 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="hidden lg:block lg:col-span-4">
              {t2 && (
                <div className="aspect-video w-full">
                  <img
                    src={t2}
                    alt="Magic book"
                    className="w-full h-full object-cover rounded border-4 border-[#351056] cursor-pointer hover:opacity-50 transition-opacity"
                  />
                </div>
              )}
            </div>
            <div className="lg:col-span-8">
              <h2 className="text-3xl md:text-4xl font-bold text-[#BB2926] mb-5">
                Volume 2: A Kind of Magic
              </h2>
              <p className="text-xl font-medium leading-relaxed mb-4">
                Volume II starts with a touching foreword from David Williamson before diving
                into Wayne&apos;s most popular, reputation-making routines. You&apos;ll get
                the inner-workings to his best original effects, the routines that helped him
                achieve his international fame.
              </p>
              <p className="mb-4">
                There&apos;s something for everyone in this book. From the close-up magic
                miracles he performed on TV, like his poetic &quot;Coins to Glass&quot; or
                one-of-a-kind &quot;Homing Death Card&quot; routine, to his ingenious
                reframing of stage illusions like his take on the &quot;Million Dollar
                Mystery&quot; and, of course, his most popular classics like &quot;Tossed Deck
                Pro&quot;.
              </p>
              <p className="mb-4">
                Wayne even shares every nuance of his go-to opening routine for winning over a
                crowd; his &quot;Five Card Repeat&quot;, which encapsulates how a pro&apos;s
                revisiting of a tired classic can create a sparkling workhorse. This is the
                routine Wayne taught to Jeff Hobson, who pronounced it, &quot;the very best
                opener for the stage magician, even today&quot;.
              </p>
              <p className="mb-4">
                For the first time ever, you&apos;ll also receive the full script and workings
                of his infamous &quot;Voices&quot; routine that he performed during his Royal
                Variety Command Performance; a routine that many believe to be one of the
                greatest magic performances ever to grace the airwaves. Unlike other
                compilations of Wayne&apos;s work, this book (and Volume III) stays true to his
                signature style. Instead of just explaining the basics of the routine, it
                dives into the necessary details and subtleties you need to truly learn the
                material. Given his limited mobility later in life, you&apos;ll also get to
                see how he modified and reworked much of his material to be hands-off.
              </p>

              <button
                type="button"
                className="text-blue-600 underline lg:hidden mb-3"
                onClick={() => toggle('v2')}
              >
                View Volume 2 contents
              </button>
              <div className={`${open.v2 ? 'block' : 'hidden'} lg:block`}>
                <h4 className="font-bold text-lg mt-4 mb-2">Contents of Volume 2</h4>
                <ul className="columns-1 md:columns-2 gap-10 list-disc pl-5 space-y-1 text-base">
                  <li>Preface (David Williamson)</li>
                  <li>Before We Begin</li>
                  <li className="list-none font-bold mt-3 -ml-5">On Stage & Television</li>
                  <li>Opener &amp; Five Card Repeat</li>
                  <li>An Englishman in New York</li>
                  <li>Banana Drama</li>
                  <li>Bottle Opener</li>
                  <li>The Crying Game</li>
                  <li>He Who Spelt It Dealt It</li>
                  <li>Invisible Deck 2.0</li>
                  <li>The Kid&apos;s a Magician</li>
                  <li>Misdirection</li>
                  <li>Nailed</li>
                  <li>Silk in Cigarette Packet</li>
                  <li>Six Pack</li>
                  <li>The Spectrumalist</li>
                  <li>This Is Your Knife</li>
                  <li>Tossed Deck Pro</li>
                  <li>Vanishing Cigarette</li>
                  <li>Voices</li>
                  <li>Sponge Ball Vanish</li>
                  <li className="list-none font-bold mt-3 -ml-5">On Stage & Television</li>
                  <li>Opener & Five Card Repeat</li>
                  <li>An Englishman in New York</li>
                  <li>Banana Drama</li>
                  <li>Bottle Opener</li>
                  <li>The Crying Game</li>
                  <li>He Who Spelt It Dealt It</li>
                  <li>Invisible Deck 2.0</li>
                  <li>The Kid&apos;s a Magician</li>
                  <li>Misdirection</li>
                  <li>Nailed</li>
                  <li>Silk in Cigarette Packet</li>
                  <li>Six Pack</li>
                  <li>The Spectrumalist</li>
                  <li>This Is Your Knife</li>
                  <li>Tossed Deck Pro</li>
                  <li>Vanishing Cigarette</li>
                  <li>Voices</li>
                  <li>Sponge Ball Vanish</li>
                  <li className="list-none font-bold mt-3 -ml-5">Wayne&apos;s Stage Illusions</li>
                  <li>Box Jumping</li>
                  <li>Asrah</li>
                  <li>Card Illusion</li>
                  <li>Heads Off</li>
                  <li>Just in Case</li>
                  <li>The Passage of Time</li>
                  <li>The Pyramid</li>
                  <li>Room Service</li>
                  <li>Sawing Through</li>
                  <li className="list-none font-bold mt-3 -ml-5">Close-up on TV</li>
                  <li>Coins to Glass</li>
                  <li>Cap & Pence</li>
                  <li>Continuous Do As I Do</li>
                  <li>Hanky Poo</li>
                  <li>The Homing Death Card</li>
                  <li>Flick-a-Flick</li>
                  <li>Razor Card</li>
                  <li className="list-none font-bold mt-2 -ml-5">TV Shows</li>
                  <li>&quot;A Kind of Magic&quot;</li>
                  <li>&quot;Wayne Dobson Close Up&quot;</li>
                </ul>
              </div>

              <p className="mt-6 flex flex-wrap gap-3">
                {renderCta()}
                <Link
                  href="/magic-books/wayne-dobsons-legacy-volume-2/"
                  className="inline-flex items-center justify-center rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg font-medium"
                >
                  Or buy Volume 2 separately
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div style={{ background: '#f2f2f2' }}>
        <div className="max-w-screen-xl mx-auto px-4 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="hidden lg:block lg:col-span-4">
              {t3 && (
                <div className="aspect-video w-full">
                  <img
                    src={t3}
                    alt="Wayne Dobson's magic"
                    className="w-full h-full object-cover rounded border-4 border-[#351056] cursor-pointer hover:opacity-50 transition-opacity"
                  />
                </div>
              )}
            </div>
            <div className="lg:col-span-8">
              <h2 className="text-3xl md:text-4xl font-bold text-[#BB2926] mb-5">
                Volume 3: Dobson&apos;s Choice
              </h2>
              <p className="text-xl font-medium leading-relaxed mb-4">
                Those who enjoy learning practical material are going to absolutely love
                Volume III. These are practical, professional routines that have been honed and
                refined in front of real audiences of all sizes. To put it simply, this is a
                book of workers. There are stand-up pieces like &quot;Total Recall&quot;,
                Wayne&apos;s unique take on the Magic Square routine, and &quot;Shopping
                List&quot;, an impossible, hilarious, and entertaining 10-minute prediction
                routine filled with a surprising wine bottle production kicker they&apos;ll
                never see coming. Wayne also includes his close-up classics like
                &quot;3Sixty&quot; and &quot;Lucky Dip&quot;, the trick that fooled Derren
                Brown (who also wrote the foreword) and immediately became one of the
                legendary mentalist&apos;s favorite go-to tricks to perform.
              </p>
              <p className="mb-4">
                You&apos;ll also get detailed instructions for creating props that are either
                unavailable or extremely difficult to purchase.
              </p>

              <button
                type="button"
                className="text-blue-600 underline lg:hidden mb-3"
                onClick={() => toggle('v3')}
              >
                View Volume 3 contents
              </button>
              <div className={`${open.v3 ? 'block' : 'hidden'} lg:block`}>
                <h4 className="font-bold text-lg mt-4 mb-2">Contents of Volume 3</h4>
                <ul className="columns-1 md:columns-2 gap-10 list-disc pl-5 space-y-1 text-base">
                  <li>Introduction (Derren Brown)</li>
                  <li>&quot;Who Are You Calling Disabled?&quot; (Bob Gill)</li>
                  <li>A Few Words from Wayne</li>
                  <li className="list-none font-bold mt-2 -ml-5">Stand-up Creations</li>
                  <li>Eye Pad</li>
                  <li>Oddball</li>
                  <li>Odds On</li>
                  <li>Shopping List</li>
                  <li>Echo</li>
                  <li>Take a Seat</li>
                  <li>Total Recall</li>
                  <li>The Unnamed Card</li>
                  <li className="list-none font-bold mt-2 -ml-5">Close-up Creations</li>
                  <li>3Sixty + 1Eighty</li>
                  <li>Anniversary Tango</li>
                  <li>Autograph</li>
                  <li>Balls of Steel</li>
                  <li>Best Friends</li>
                  <li>Boston Blow</li>
                  <li>Bullshit</li>
                  <li>City Break</li>
                  <li>Compatability</li>
                  <li>Die Vernon</li>
                  <li>Dobbo Marked Deck Systems</li>
                  <li>Fifty/Fifty</li>
                  <li>Fluke</li>
                  <li>The F--k Trick</li>
                  <li>Heads Up 2.0</li>
                  <li>Houdini&apos;s Diary</li>
                  <li>Houdini&apos;s Watch</li>
                  <li>The Invisible Lie Detector</li>
                  <li>I&apos;ve Got Your Number</li>
                  <li>Lucky Card</li>
                  <li>Lucky Dip</li>
                  <li>Open Face</li>
                  <li>Out of My Hands</li>
                  <li>Possessions</li>
                  <li>Rainbow</li>
                  <li>Second Chance</li>
                  <li>Startling</li>
                  <li>Stunning</li>
                  <li>Sweet</li>
                  <li>Synchronicity</li>
                  <li>The Man Who Knows</li>
                  <li>T.L.C.</li>
                  <li>Visionary</li>
                  <li>Vodka & Coke</li>
                  <li>White Lightning</li>
                  <li>Keep It Zipped</li>
                  <li>Fifty Shades of Gold</li>
                </ul>
              </div>

              <p className="mt-6 flex flex-wrap gap-3">
                {renderCta()}
                <Link
                  href="/magic-books/wayne-dobsons-legacy-volume-3/"
                  className="inline-flex items-center justify-center rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg font-medium"
                >
                  Or buy Volume 3 separately
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
